import './ExperienceSelector.scss';
import React, { useState, useEffect, useMemo } from 'react';
import { FlightTextInput } from '@flybits/design-system';
import { ChartConfigState, ExperienceSelectorItem } from 'pages/Analytics/types';
import Skeleton from 'react-loading-skeleton';

const MAIN_CLASS = 'experience-selector';
const CLASSES = {
  LABEL: `${MAIN_CLASS}__label`,
  SELECT: `${MAIN_CLASS}__select`,
};

interface ExperienceSelectorProps {
  chartConfig: ChartConfigState;
  experiences?: ExperienceSelectorItem[];
  onChange?: (experiences: ExperienceSelectorItem[]) => void;
  isMultiple?: boolean;
  onValidation: (isValid: boolean) => void;
  isLoading?: boolean;
}

/**
 * ExperienceSelector component allows users to search and select experiences from a list.
 *
 * @param {ExperienceSelectorProps} props - The properties for the ExperienceSelector component.
 * @param {ExperienceSelectorItem[]} [props.chartConfig] - Chart config UI state
 * @param {ExperienceSelectorItem[]} [props.experiences] - An optional array of experience items to display in the selector.
 * @param {(exps: ExperienceSelectorItem[]) => void} [props.onChange] - An optional callback function that is called when the selected experiences change.
 * @param {boolean} [props.isMultiple] - An optional boolean indicating if multiple selections are allowed.
 * @param {function} props.onValidation - Callback function to send validation status to parent component.
 * @param {function} props.isLoading - An optional boolean indicating if the experience selector list is loading.
 *
 */
export default function ExperienceSelector({
  chartConfig,
  experiences,
  isMultiple,
  onChange,
  onValidation,
  isLoading,
}: ExperienceSelectorProps) {
  const { selectedExperiences } = chartConfig;
  const initialSelected = useMemo(
    () => selectedExperiences?.map((experience) => experience.name) || [],
    [selectedExperiences],
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    const isValid = selected.length > 0 || initialSelected.length > 0;
    onValidation(isValid);
  }, [selected, initialSelected, onValidation]);
  return (
    <div className={MAIN_CLASS}>
      <label className={CLASSES.LABEL} htmlFor="experience-list">
        Select an Experience <span>*</span>
      </label>
      <FlightTextInput
        labelId={'experience-search'}
        iconInput="search"
        width="100%"
        value={searchTerm}
        onChange={(evt: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(evt.target.value)}
        placeholderText="Search"
      />
      {isLoading ? (
        <div
          id="experience-list"
          className={CLASSES.SELECT}
          role="status"
          aria-busy="true"
          aria-label="Loading experiences"
        >
          <Skeleton width="100%" height="100%" />
        </div>
      ) : (
        <select
          className={CLASSES.SELECT}
          id="experience-list"
          aria-labelledby="experience-list"
          multiple={!!isMultiple}
          size={5}
          defaultChecked={false}
          value={selected.length ? selected : initialSelected.length ? initialSelected : undefined}
          onChange={(e) => {
            const selectedValue = Array.from(e.target.selectedOptions).map((option) => option.value);
            setSelected(selectedValue);
            onChange && onChange(experiences?.filter((experience) => selectedValue.includes(experience.name)) ?? []);
          }}
        >
          {experiences
            ?.filter((experience) => experience.name.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()))
            .map(({ key, name }) => (
              <option key={key} value={name}>
                {name}
              </option>
            ))}
        </select>
      )}
    </div>
  );
}
